'use client';

/**
 * useCouponHandler - Unified hook for coupon/voucher apply, remove, and toggle operations.
 *
 * Consolidates all coupon mutation logic into a single hook:
 * - Uses `useApplyCode` (marketing) for applying coupons by code
 * - Uses `useApplyVoucher` (marketing) for applying vouchers by allocation ID
 * - Uses `useRemoveCode` (marketing) for removing both coupons and vouchers
 *
 * After every successful mutation (apply/remove), calls `onRefresh` to refetch
 * applied codes and trigger fast cart total updates so the UI stays in sync.
 *
 * @module @kidslabs/module-flash-sale/modules/sales-rule/hooks/use-coupon-handler
 */

import { useCallback, useState, useEffect, useMemo } from 'react';
import { type ApolloError } from '@apollo/client';
import { use$ } from '@legendapp/state/react';
import { useCouponContext } from '../contexts/coupon.context';
import { type AppliedUniversalCode } from '../../../types/front.graphql';
import { type AppliedVoucher, type MarketingAppliedCoupon, MarketingCodeType } from '../../../types/marketing.graphql';
import { useApplyCode } from './use-apply-code';
import { useApplyVoucher } from './use-apply-voucher';
import { useRemoveCode } from './use-remove-code';
import { useTranslation } from 'next-i18next';
import type { DialogOptions } from '@kidslabs/mario-nextjs-react-ui-core';

interface UseCouponHandlerProps {
    onSuccess: () => void;
    appliedCoupons?: (AppliedUniversalCode | MarketingAppliedCoupon)[];
    appliedVouchers?: AppliedVoucher[];
    setDialogOptions: (options: DialogOptions) => void;
    showDialog: () => void;
    hideDialog: () => void;
    /** Callback to refetch applied codes and fast cart totals after coupon changes */
    onRefresh?: () => void;
    /** Track applied code for optimistic display */
    trackAppliedCode?: (voucherId: string) => void;
}

export const useCouponHandler = ({
    onSuccess,
    appliedCoupons = [],
    appliedVouchers = [],
    setDialogOptions,
    showDialog,
    onRefresh,
    trackAppliedCode,
}: UseCouponHandlerProps) => {
    const couponContext = useCouponContext();
    const { t } = useTranslation('flash-sale');
    const [couponText, setCouponText] = useState('');
    const selectedCoupon = use$(couponContext?.couponState$?.selectedCoupon);
    const setLoading = couponContext?.setLoading;

    // ═══════════════════════════════════════════════════════════════════════
    // Core mutation hooks (unified — no legacy duplication)
    // ═══════════════════════════════════════════════════════════════════════

    const {
        applyCode,
        applyLoading: applyCodeLoading,
        errorMessage: applyCodeErrorMessage,
        setErrorMessage: setApplyErrorMessage,
    } = useApplyCode();

    const {
        applyVoucher,
        applyLoading: applyVoucherLoading,
        errorMessage: applyVoucherErrorMessage,
    } = useApplyVoucher();

    const {
        removeCode,
        removeLoading: removeCodeLoading,
        errorMessage: removeCodeErrorMessage,
        setErrorMessage: setRemoveErrorMessage,
    } = useRemoveCode();

    // ═══════════════════════════════════════════════════════════════════════
    // Derived loading and error state
    // ═══════════════════════════════════════════════════════════════════════

    const isApplyLoading = applyCodeLoading || applyVoucherLoading;
    const isRemoveLoading = removeCodeLoading;
    const isAnyLoading = isApplyLoading || isRemoveLoading;

    const errorMessage = applyCodeErrorMessage || applyVoucherErrorMessage || removeCodeErrorMessage;

    useEffect(() => {
        setLoading?.('add', isApplyLoading);
        setLoading?.('remove', isRemoveLoading);
    }, [isApplyLoading, isRemoveLoading, setLoading]);

    // ═══════════════════════════════════════════════════════════════════════
    // Currently applied code references (convenience accessors)
    // ═══════════════════════════════════════════════════════════════════════

    const currentCouponCode = appliedCoupons[0]?.code;
    const currentVoucherId = appliedVouchers[0]?.voucher_id;
    const currentVoucherAllocationName = appliedVouchers[0]?.allocation_name;
    const currentVoucherAllocationId = appliedVouchers[0]?.allocation?.id;

    // ═══════════════════════════════════════════════════════════════════════
    // Shared helpers
    // ═══════════════════════════════════════════════════════════════════════

    const showErrorDialog = useCallback(
        (message: string) => {
            setDialogOptions({
                title: t('text_notification', { defaultValue: 'Thông báo' }),
                content: message,
                autoDismissMilliseconds: 2000,
                titleIcon: 'alert',
            });
            showDialog();
        },
        [setDialogOptions, showDialog, t]
    );

    /** Centralized error handler — sets error message state and shows dialog. */
    const handleOperationError = useCallback(
        (error: unknown, fallbackKey = 'text_coupon_action_failed') => {
            const message = (error as ApolloError)?.message || (error as Error)?.message || t(fallbackKey);
            setApplyErrorMessage(message);
            showErrorDialog(message);
        },
        [t, setApplyErrorMessage, showErrorDialog]
    );

    const handleOnChangeText = useCallback(
        (text: string) => {
            setCouponText(text);
            setApplyErrorMessage(undefined);
            setRemoveErrorMessage(undefined);
        },
        [setApplyErrorMessage, setRemoveErrorMessage]
    );

    // ═══════════════════════════════════════════════════════════════════════
    // Remove current applied code (coupon or voucher)
    // removeCode already calls incrementDiscountVersion internally.
    // ═══════════════════════════════════════════════════════════════════════

    const handleRemoveCurrentCode = useCallback(async () => {
        if (currentCouponCode) {
            await removeCode(currentCouponCode, MarketingCodeType.COUPON);
        }
        if (currentVoucherId) {
            await removeCode(currentVoucherId, MarketingCodeType.VOUCHER);
        }
    }, [currentCouponCode, currentVoucherId, removeCode]);

    // ═══════════════════════════════════════════════════════════════════════
    // Reapply previous code on failed apply (rollback)
    // ═══════════════════════════════════════════════════════════════════════

    const reapplyPreviousCode = useCallback(async () => {
        try {
            if (currentCouponCode) {
                await applyCode(currentCouponCode);
            } else if (currentVoucherAllocationId) {
                await applyVoucher(currentVoucherAllocationId);
            }
        } catch (rollbackError) {
            handleOperationError(rollbackError);
        }
    }, [currentCouponCode, currentVoucherAllocationId, applyCode, applyVoucher, handleOperationError]);

    // ═══════════════════════════════════════════════════════════════════════
    // Core operation: remove current → apply new → rollback on failure
    // Shared by handleApplyCode and handleSelectedCouponPress.
    // ═══════════════════════════════════════════════════════════════════════

    const handleRemoveThenApply = useCallback(
        async (applyFunction: () => Promise<void>, trackingId?: string) => {
            try {
                await handleRemoveCurrentCode();
            } catch (removeError) {
                handleOperationError(removeError, 'text_coupon_remove_failed');
                return;
            }

            try {
                await applyFunction();
                if (trackingId) {
                    trackAppliedCode?.(trackingId);
                }
            } catch (applyError) {
                await reapplyPreviousCode();
                throw applyError;
            }
        },
        [handleRemoveCurrentCode, reapplyPreviousCode, trackAppliedCode, handleOperationError]
    );

    /** Clear input text, refetch applied codes / fast cart totals, and call onSuccess. */
    const handleCompleteAndRefresh = useCallback(() => {
        setCouponText('');
        onRefresh?.();
        onSuccess();
    }, [onRefresh, onSuccess]);

    // ═══════════════════════════════════════════════════════════════════════
    // Public: Apply a coupon/voucher code entered by the user.
    // If the same code is already applied, toggle it off (remove).
    // ═══════════════════════════════════════════════════════════════════════

    const handleApplyCode = useCallback(
        async (value: string) => {
            try {
                const code = value?.trim();
                if (!code) return;

                // Toggle off: same coupon code → remove
                if (currentCouponCode && code === currentCouponCode) {
                    await removeCode(currentCouponCode, MarketingCodeType.COUPON);
                    handleCompleteAndRefresh();
                    return;
                }

                // Toggle off: same voucher allocation name → remove
                if (currentVoucherId && code === currentVoucherAllocationName) {
                    await removeCode(currentVoucherId, MarketingCodeType.VOUCHER);
                    handleCompleteAndRefresh();
                    return;
                }

                // Remove current → apply new code
                await handleRemoveThenApply(() => applyCode(code), code);
                handleCompleteAndRefresh();
            } catch (error) {
                handleOperationError(error);
            }
        },
        [
            currentCouponCode,
            currentVoucherId,
            currentVoucherAllocationName,
            removeCode,
            handleRemoveThenApply,
            applyCode,
            handleCompleteAndRefresh,
            handleOperationError,
        ]
    );

    // ═══════════════════════════════════════════════════════════════════════
    // Public: Apply coupon selected from the coupon list screen.
    // ═══════════════════════════════════════════════════════════════════════

    const handleSelectedCouponPress = useCallback(async () => {
        try {
            // No coupon selected → remove currently applied code
            if (!selectedCoupon) {
                if (currentCouponCode) {
                    await removeCode(currentCouponCode, MarketingCodeType.COUPON);
                    handleCompleteAndRefresh();
                    return;
                }
                if (currentVoucherId) {
                    await removeCode(currentVoucherId, MarketingCodeType.VOUCHER);
                    handleCompleteAndRefresh();
                    return;
                }
                return;
            }

            // Already applied same code → just close
            if (selectedCoupon.code === currentCouponCode || selectedCoupon.code === currentVoucherAllocationName) {
                onSuccess();
                return;
            }

            // Determine apply strategy: voucher (by allocation ID) or coupon (by code)
            const allocationId = selectedCoupon.id;

            if (allocationId) {
                await handleRemoveThenApply(() => applyVoucher(allocationId), allocationId);
            } else {
                const couponCode = selectedCoupon.code ?? '';
                await handleRemoveThenApply(() => applyCode(couponCode), couponCode || undefined);
            }

            handleCompleteAndRefresh();
        } catch (error) {
            handleOperationError(error);
        }
    }, [
        selectedCoupon,
        currentCouponCode,
        currentVoucherId,
        currentVoucherAllocationName,
        removeCode,
        onSuccess,
        handleRemoveThenApply,
        applyVoucher,
        applyCode,
        handleCompleteAndRefresh,
        handleOperationError,
    ]);

    // ═══════════════════════════════════════════════════════════════════════
    // Public: Remove the currently applied coupon (for external callers).
    // ═══════════════════════════════════════════════════════════════════════

    const handleRemoveAppliedCode = useCallback(async () => {
        try {
            await handleRemoveCurrentCode();
            setCouponText('');
            onRefresh?.();
            onSuccess();
        } catch (error) {
            handleOperationError(error, 'text_coupon_remove_failed');
        }
    }, [handleRemoveCurrentCode, onRefresh, onSuccess, handleOperationError]);

    // ═══════════════════════════════════════════════════════════════════════
    // Computed state
    // ═══════════════════════════════════════════════════════════════════════

    const isSelectedCoupon = (currentCouponCode || currentVoucherId) && !selectedCoupon;

    const isButtonDisabled = useMemo(
        () => isAnyLoading || (!currentCouponCode && !currentVoucherId && !selectedCoupon),
        [isAnyLoading, currentCouponCode, currentVoucherId, selectedCoupon]
    );

    return {
        couponText,
        errorMessage,
        isButtonDisabled,
        removeLoading: isRemoveLoading,
        applyLoading: isApplyLoading,
        handleOnChangeText,
        handleApplyCode,
        handleRemoveAppliedCode,
        isSelectedCoupon,
        handleSelectedCouponPress,
    };
};
